import {FC, useCallback, useEffect, useMemo, useState} from "react";
// components
import PageHeading from "../components/PageHeading";
import Button from "../components/Button";
// styles
import styles from "../styles/pages/estimation/EstimationPage.module.css";
import BasketInformation from "../components/BasketInformation";
import {IBasket} from "../@interfaces/basket.interface";
import {IQuote} from "../@interfaces/purchase.interface";
import Footer from "../components/Footer";
import {formatMoney, getCurrencySymbol, SUPPORTED_CURRENCIES} from "../utils/currency";
import Select from "../components/Select";
import {IWidgetConfiguration} from "../merchantConfig";
import {formatNoDecimals} from "../utils/number";

interface IEstimationPage {
  quote: IQuote | null;
  offsetAmount: number;
  selectedCurrency: any;
  isLoading: boolean;
  basket: IBasket | null;
  onOffsetClick: () => void;
  merchantCode: string;
  setSelectedCurrency: (currency: string) => void;
  setOffsetAmount: (amount: number) => void;
  widgetConfiguration: IWidgetConfiguration
  setActiveStep: (step: number) => void;
  isZeroDecimalCurrency: boolean;
  quotingErrorMessage: string | null;
}

const EstimationPage: FC<IEstimationPage> = ({
  offsetAmount,
  quote,
  selectedCurrency,
  isLoading,
  basket,
  onOffsetClick,
  merchantCode,
  setSelectedCurrency,
  setOffsetAmount,
  widgetConfiguration ,
  setActiveStep,
  isZeroDecimalCurrency,
  quotingErrorMessage
}) => {

  const [fieldValue,setFieldValue] = useState<string>(offsetAmount.toFixed(isZeroDecimalCurrency ? 0 : 2));
  const [validationErrorMessage,setValidationErrorMessage] = useState<string>('');

  const onFieldUpdate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    let value = (e.target.value || '')
        .replace(/[^0-9.]/g,'') // remove non-digit symbols
        .replace(/(\.[.]+)/g, '.') // remove excess decimal dotts
        .replace((isZeroDecimalCurrency ? /(\d*).*$/ : /(\d*\.\d{1,2}).*$/),'$1') // remove excess decimal places
    setFieldValue(value);
  }, [isZeroDecimalCurrency])

  useEffect(() => {
    const toNumber = Number(fieldValue);
    if (Number.isNaN(toNumber) || toNumber <= 0) {
      setValidationErrorMessage("Please, specify valid amount");
      setOffsetAmount(0);
    } else {
      setValidationErrorMessage("");
      setOffsetAmount(toNumber);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [fieldValue])

  useEffect(() => {
    if (Number(offsetAmount) !== Number(fieldValue)) { 
      setFieldValue(offsetAmount.toFixed(isZeroDecimalCurrency ? 0 : 2));
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [offsetAmount])


  const renderCurrencyList = useMemo(() =>
    SUPPORTED_CURRENCIES.map((currency) => ({ value: currency, name: currency }))
  , []);

  //Please don't turn this into html (eg adding bold tags) as it will break the translation
  function getSummaryText() {
    if(offsetAmount && quote && !validationErrorMessage && !quotingErrorMessage && !isLoading){
      return `Your ${quote?.offsetPrice?.currency && getCurrencySymbol(quote.offsetPrice.currency)}${formatMoney(quote?.offsetPrice?.value, isZeroDecimalCurrency)} ${quote?.offsetPrice?.currency?.toUpperCase()} contribution offsets ${quote?.carbonOffsetQuantity?.value && (formatNoDecimals(Math.round(quote?.carbonOffsetQuantity.value)))} kg of CO₂ emissions.`
    }
    else{
      return ""
    }
  }

  return (
      <div className={`${styles.container} delayed-fadein`}>
        <div>
          <PageHeading
            title={'Your contribution'}
            activeStep={0}
            setActiveStep={setActiveStep}
          />

          <div className={styles.marginTop}>
            <p className={styles.textBold}>{"Please confirm your contribution."}</p>
          </div>

          <div className={`${styles.selectWrapper} ${validationErrorMessage ? styles.selectWrapperError : ''}`}>
            <input
              // do NOT add `type={"number"}` - browser doesnt send events for invalid amount
              inputMode={"numeric"}
              step={isZeroDecimalCurrency ? "1" : "0.01"}
              className={styles.amountInput}
              value={fieldValue}
              onChange={onFieldUpdate}
              placeholder={"Amount..."}
            />
            <Select
              onChange={(value) => {
                  setSelectedCurrency(value);
              }}
              value={selectedCurrency}
              optionsList={renderCurrencyList}
              disabled={false}
              className={'no-translate'}
            />
          </div>
          <div className={`${styles.text} ${styles.errorMessage}`} style={{marginBottom: '-1rem'}}>
            {validationErrorMessage || <>&nbsp;</>}
          </div>
          <div className={styles.marginTop}>
            <p className={styles.text}>{"You are supporting real projects that make a meaningful difference for the planet."}</p>
            <BasketInformation merchantCode={merchantCode} projects={basket?.projects} hideLink={true}/>
          </div>
      </div>
      <div className={styles.controls}>
      <p className={`${styles.text} ${quotingErrorMessage ? styles.errorMessage : ''} ${!isLoading ? 'delayed-fadein' : ''}`}> 
        {/*isLoading condition ensures that a new quote loading removes then re-adds the fadein class for non-english*/}
        {quotingErrorMessage || getSummaryText()}
      </p>
        <div className={styles.marginTop}>
          <Button
            variant={"primary"}
            isLoading={isLoading}
            text={"Continue to payment"}
            onClick={() => onOffsetClick()}
            disabled={isLoading || !offsetAmount || !quote || !!validationErrorMessage || !!quotingErrorMessage}
          />
        </div>
      </div>

      {!widgetConfiguration.hidePoweredBy && <Footer/>}
    </div>
  );
};

export default EstimationPage;
